<template>
  <div class="tag-selector">
    <div class="selector-trigger" :class="{ 'disabled': disabled, 'has-value': modelValue }" @click="toggleDropdown">
      <div class="selected-tag" v-if="selectedTag">
        <i :class="selectedTag.tagIcon || 'ri-price-tag-3-line'" :style="{ color: selectedTag.tagColor || '#666' }"></i>
        <span>{{ selectedTag.tagName }}</span>
      </div>
      <span v-else-if="modelValue">{{ modelValue }}</span>
      <span v-else class="placeholder">{{ placeholder }}</span>
      <i class="ri-arrow-down-s-line toggle-arrow" :class="{ 'open': showDropdown }"></i>
    </div>

    <div v-if="showDropdown" class="dropdown-panel">
      <div class="panel-header">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="allowCreate ? '搜尋或輸入新名稱' : '搜尋標籤...'"
          @input="handleSearch"
        />
        <button type="button" class="btn-clear" @click="clearSelection">
          清除
        </button>
      </div>

      <div v-if="allowCreate" class="create-bar">
        <button
          type="button"
          class="btn-create"
          :disabled="!canCreate || creating"
          @click="createTag"
        >
          <i class="ri-add-line"></i>
          {{ creating ? '建立中...' : `新增${createLabel}` }}
        </button>
        <span class="create-hint">{{ createHint }}</span>
      </div>
      <p v-if="createError" class="create-error">{{ createError }}</p>

      <!-- 標籤來源頁籤：已指定來源時不再切換 -->
      <div v-if="!tagSource" class="tag-tabs">
        <button
          v-for="tab in tagTabs"
          :key="tab.key"
          type="button"
          :class="['tag-tab', { active: activeTab === tab.key }]"
          @click="activeTab = tab.key; filterTags()"
          :title="tab.label"
        >
          <i :class="tab.icon"></i>
          <span class="tab-label">{{ tab.label }}</span>
        </button>
      </div>

      <!-- 標籤列表 -->
      <div class="tags-list">
        <div
          v-for="tag in filteredTags"
          :key="tag.id"
          class="tag-item"
          :class="{ 'selected': modelValue === tag.tagName }"
          @click="selectTag(tag)"
        >
          <div class="tag-info">
            <i :class="tag.tagIcon || 'ri-price-tag-3-line'" :style="{ color: tag.tagColor || '#666' }"></i>
            <span class="tag-name">{{ tag.tagName }}</span>
            <span v-if="tag.tagSource === 'manual'" class="tag-badge manual-badge">互動</span>
            <span v-else-if="tag.tagSource === 'auto_rule'" class="tag-badge auto-badge">自動</span>
            <span v-else-if="tag.tagSource === 'exclusive'" class="tag-badge exclusive-badge">專屬</span>
          </div>
          <div v-if="modelValue === tag.tagName" class="check-icon">
            <i class="ri-check-line"></i>
          </div>
        </div>
            <div v-if="filteredTags.length === 0 && !canCreate" class="empty-state">
              <p>{{ searchQuery ? '找不到符合的標籤' : emptyLabel }}</p>
              <p v-if="!tagSource" class="hint">請先到「標籤管理」建立標籤</p>
            </div>
          </div>
        </div>

    <!-- 點擊外部關閉 -->
    <div v-if="showDropdown" class="dropdown-overlay" @click="closeDropdown"></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { tagService } from '@/api/modules/tags';

export default {
  name: 'TagSelector',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '請選擇標籤'
    },
    allowCreate: {
      type: Boolean,
      default: true
    },
    tagSource: {
      type: String,
      default: null // null 表示顯示互動、自動規則與專屬標籤；'manual'、'auto_rule'、'exclusive' 表示只顯示特定來源
    }
  },
  emits: ['update:modelValue', 'tag-selected'],
  setup(props, { emit }) {
    const showDropdown = ref(false);
    const searchQuery = ref('');
    const activeTab = ref('all');
    const tags = ref([]);
    const loading = ref(false);
    const creating = ref(false);
    const createError = ref('');

    // 標籤來源頁籤
    const tagTabs = [
      { key: 'all', label: '全部標籤', icon: 'ri-list-check' },
      { key: 'manual', label: '互動標籤', icon: 'ri-hand-heart-line' },
      { key: 'auto_rule', label: '自動標籤', icon: 'ri-robot-line' },
      { key: 'exclusive', label: '專屬標籤', icon: 'ri-store-2-line' }
    ];

    // 載入標籤列表
    const loadTags = async () => {
      loading.value = true;
      try {
        const params = { isActive: true };
        if (props.tagSource) {
          params.tagSource = props.tagSource;
        }
        const response = await tagService.getTags(params);
        tags.value = response.data || [];
      } catch (error) {
        console.error('載入標籤失敗:', error);
        tags.value = [];
      } finally {
        loading.value = false;
      }
    };

    const canCreate = computed(() => {
      if (!props.allowCreate) return false;
      const name = searchQuery.value.trim();
      if (!name || name.length > 100) return false;
      return !tags.value.some(tag => tag.tagName === name);
    });

    const createLabel = computed(() => (
      props.tagSource === 'auto_rule' ? '自動規則標籤' : '標籤'
    ));

    const createHint = computed(() => {
      const name = searchQuery.value.trim();
      if (!name) return '先在上方輸入新名稱';
      if (tags.value.some(tag => tag.tagName === name)) return '此名稱已存在，請從下方選擇';
      return `將建立「${name}」`;
    });

    const emptyLabel = computed(() => {
      const labels = {
        auto_rule: '沒有自動規則標籤',
        manual: '沒有互動標籤',
        exclusive: '沒有專屬標籤'
      };
      return labels[props.tagSource] || '沒有可用標籤';
    });

    // 選中的標籤
    const selectedTag = computed(() => {
      if (!props.modelValue) return null;
      return tags.value.find(tag => tag.tagName === props.modelValue);
    });

    // 篩選標籤
    const filteredTags = computed(() => {
      let filtered = tags.value.filter(tag => tag.tagSource !== 'system');

      // 根據頁籤篩選
      if (activeTab.value !== 'all') {
        filtered = filtered.filter(tag => tag.tagSource === activeTab.value);
      }

      // 根據搜尋關鍵字篩選
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase().trim();
        filtered = filtered.filter(tag =>
          tag.tagName.toLowerCase().includes(query) ||
          (tag.description && tag.description.toLowerCase().includes(query))
        );
      }

      return filtered;
    });

    const toggleDropdown = () => {
      if (props.disabled) return;
      showDropdown.value = !showDropdown.value;
      if (showDropdown.value) {
        searchQuery.value = '';
        activeTab.value = 'all';
        loadTags();
      }
    };

    const closeDropdown = () => {
      showDropdown.value = false;
    };

    const createTag = async () => {
      const name = searchQuery.value.trim();
      if (!props.allowCreate || creating.value || !name) return;
      creating.value = true;
      createError.value = '';
      try {
        const response = await tagService.createTag({
          tagName: name,
          tagSource: props.tagSource || 'manual',
          isActive: true
        });
        const tag = response?.data;
        if (!tag?.tagName) {
          createError.value = response?.message || '建立標籤失敗';
          return;
        }
        tags.value = [tag, ...tags.value.filter(item => item.id !== tag.id)];
        emit('update:modelValue', tag.tagName);
        emit('tag-selected', tag);
        closeDropdown();
      } catch (error) {
        createError.value = error.message || '建立標籤失敗';
      } finally {
        creating.value = false;
      }
    };

    const selectTag = (tag) => {
      emit('update:modelValue', tag.tagName);
      emit('tag-selected', tag); // 發送選中的標籤完整資訊
      closeDropdown();
    };

    const clearSelection = () => {
      emit('update:modelValue', '');
      emit('tag-selected', null); // 清除選擇時也清除標籤資訊
      closeDropdown();
    };

    const handleSearch = () => {
      // 搜尋功能已透過 computed 實現
    };

    const filterTags = () => {
      // 篩選功能已透過 computed 實現
    };

    // 監聽 modelValue 變化，確保標籤列表已載入
    watch(() => props.modelValue, (newValue) => {
      if (newValue && !selectedTag.value) {
        if (tags.value.length === 0) {
          loadTags();
        }
      }
    });

    onMounted(() => {
      // 如果已有值，載入標籤列表以顯示選中的標籤資訊
      if (props.modelValue) {
        loadTags();
      }
    });

    return {
      showDropdown,
      searchQuery,
      activeTab,
      tags,
      loading,
      tagTabs,
      selectedTag,
      emptyLabel,
      canCreate,
      createLabel,
      createHint,
      creating,
      createError,
      filteredTags,
      createTag,
      toggleDropdown,
      closeDropdown,
      selectTag,
      clearSelection,
      handleSearch,
      filterTags
    };
  }
};
</script>

<style scoped>
.tag-selector {
  position: relative;
  width: 100%;
}

.selector-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 40px;
}

.selector-trigger:hover:not(.disabled) {
  border-color: #007bff;
}

.selector-trigger.disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.selector-trigger.has-value {
  border-color: #007bff;
}

.selected-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.selected-tag i {
  font-size: 18px;
}

.placeholder {
  color: #999;
  flex: 1;
}

.toggle-arrow {
  transition: transform 0.2s;
  color: #666;
}

.toggle-arrow.open {
  transform: rotate(180deg);
}

.dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.search-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.btn-clear {
  padding: 8px 16px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-clear:hover {
  background: #e9ecef;
}

.tag-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  flex-wrap: nowrap;
  min-width: 0;
}

.tag-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 10px;
  background: #f5f5f5;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
  transition: all 0.2s;
  min-width: 0;
  flex-shrink: 1;
}

.tag-tab:hover {
  background: #e9ecef;
}

.tag-tab.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.tag-tab i {
  font-size: 16px;
}

.tab-label {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tags-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.tag-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin-bottom: 4px;
  border: 1px solid #eee;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.tag-item:hover {
  background: #f5f7fa;
  border-color: #007bff;
}

.tag-item.selected {
  background: #e7f3ff;
  border-color: #007bff;
}

.create-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  background: #f8fbff;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 8px 12px;
  background: #0d6efd;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-create:disabled {
  background: #b6d4fe;
  cursor: not-allowed;
}

.create-hint {
  color: #666;
  font-size: 13px;
}

.create-error {
  margin: 0;
  padding: 8px 16px;
  color: #c62828;
  font-size: 13px;
}

.tag-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.tag-info i {
  font-size: 18px;
}

.tag-name {
  font-size: 14px;
  color: #333;
}

.tag-badge {
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
}

.manual-badge {
  background: #e3f2fd;
  color: #1976d2;
}

.auto-badge {
  background: #f3e5f5;
  color: #7b1fa2;
}

.exclusive-badge {
  background: #e8f5e9;
  color: #2e7d32;
}

.system-badge {
  background: #fff3e0;
  color: #e65100;
}

.check-icon {
  color: #007bff;
  font-size: 18px;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #999;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.empty-state .hint {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  background: transparent;
}
</style>
